import { FACTIONS, FATIGUE_MAX, type Faction } from './constants.ts';
import { xpForLevel } from './progression.ts';
import type { PlayerState } from './player.ts';

/**
 * A best-of, which is the thing a lobby actually plays.
 *
 * `bestOf` and `carryXp` were settings with nowhere to land: a match was one
 * round and ended, so "best of three" was a number in a form and nothing else.
 * This is the layer that makes them mean something — how many rounds have gone,
 * who has won them, which map is next, and what a mage keeps when the next one
 * starts.
 *
 * Kept apart from `MatchState` on purpose. A match knows about shrines, pools
 * and a clock; a series knows about none of those and only about matches. Mixing
 * them would mean every test that wanted a round had to set up a tournament.
 */

export interface SeriesState {
  /** 1, 3 or 5. A round is won outright, so it takes ceil(n/2) to take the set. */
  bestOf: number;
  /** Maps in rotation. A round is played on `pool[index % pool.length]`. */
  pool: string[];
  index: number;
  /** Rounds finished, and who took each. */
  played: number;
  wins: Record<Faction, number>;
  /** Whether levels earned survive into the next round. See `carryInto`. */
  carryXp: boolean;
}

export function makeSeries(
  bestOf: number, pool: string[], carryXp = true,
): SeriesState {
  return {
    bestOf,
    pool: pool.length ? [...pool] : ['great-cathedral'],
    index: 0,
    played: 0,
    wins: { order: 0, balance: 0, chaos: 0 },
    carryXp,
  };
}

/** The map this round is played on. */
export function currentMap(s: SeriesState): string {
  return s.pool[s.index % s.pool.length]!;
}

/**
 * How many rounds it takes to win the set.
 *
 * Two of three, three of five. With three factions a side can take the set
 * without winning a majority of the rounds played — two of three is enough even
 * if the other four went elsewhere — which is correct: the alternative is a
 * three-way series that can only end on a timer.
 */
export function roundsToWin(bestOf: number): number {
  return Math.ceil(bestOf / 2);
}

/** Whoever has taken enough rounds, if anybody has. */
export function seriesWinner(s: SeriesState): Faction | null {
  const needed = roundsToWin(s.bestOf);
  for (const f of FACTIONS) {
    if (s.wins[f] >= needed) return f;
  }
  return null;
}

/**
 * Whether the set is finished.
 *
 * Either somebody has taken enough rounds, or every round has been played and
 * nobody has — which happens when three sides split them, and is a draw rather
 * than a reason to keep going. A series that plays on until somebody dominates
 * is a series with no end.
 */
export function seriesOver(s: SeriesState): boolean {
  return seriesWinner(s) !== null || s.played >= s.bestOf;
}

/** Records a round and moves the rotation on. */
export function endRound(s: SeriesState, winner: Faction | null): void {
  s.played += 1;
  if (winner) s.wins[winner] += 1;
  s.index += 1;
}

/**
 * What a mage keeps into the next round.
 *
 * The toggle a host set, applied. Carried, a side that took the first round
 * comes into the second with the levels it earned — which snowballs, and is the
 * point in a room that is there to reach levels nobody sees in ten minutes.
 * Reset, every round starts level and the set is decided by play rather than by
 * who got ahead first.
 *
 * Everything else is reset either way. Health, power, position and effects
 * belong to a round; a mage carrying a burn across a scoreboard would be a bug
 * in any lobby.
 */
export function carryInto(players: PlayerState[], carryXp: boolean): void {
  for (const p of players) {
    if (!carryXp) {
      p.xp = 0;
      p.level = 1;
    }

    p.hp = p.hpMax;
    p.pp = p.ppMax;
    p.fatigue = FATIGUE_MAX;
    p.effects = [];
    p.alive = true;
    p.ghost = false;
    p.inValhalla = false;
    p.pendingImpulse = null;
    p.verticalVelocity = 0;
    p.vel = { x: 0, y: 0, z: 0 };
  }
}

/**
 * The floor a carried mage comes back at.
 *
 * Exported so a lobby can say what carrying actually buys before anybody agrees
 * to it: a level, in this game, is a spell tier and a hit-point pool, and
 * "levels carry over" means little to somebody who has not seen what a level is
 * worth.
 */
export function xpAtLevel(level: number): number {
  return xpForLevel(level);
}


// ── Arriving late ────────────────────────────────────────────────────────────

/**
 * What level somebody dropping into a match in progress starts at.
 *
 * The lowest currently in play, not the first level. When experience carries
 * across rounds a match can be well into its second round with everybody at
 * eight, and a newcomer at one is not playing the same game as the people
 * shooting at them — they are target practice, and they leave.
 *
 * The lowest rather than the average because the average can be farmed: arrive,
 * die, leave, arrive again. The lowest can only ever be matched, never exceeded,
 * so there is nothing to work at.
 */
export function joinLevel(players: readonly PlayerState[]): number {
  const inPlay = players.filter((p) => p.level > 0);
  if (!inPlay.length) return 1;
  return Math.max(1, Math.min(...inPlay.map((p) => p.level)));
}

/**
 * How long a mage's progress is kept after they vanish.
 *
 * This exists for dropped connections, which are common and are currently
 * punished absurdly: a player whose wifi blinks at minute eight comes back as a
 * fresh drop-in and loses everything they earned. Remembering them for a couple
 * of minutes turns a disconnect into an interruption.
 *
 * It closes the class-swapping hole as a side effect, which is worth noting but
 * is not the reason. Somebody leaving and rejoining to change discipline gets
 * their old discipline back, because as far as the room is concerned they never
 * really left.
 */
export const REJOIN_SECONDS = 150;

/** What is held for somebody who might come back. */
export interface Departed {
  xp: number;
  level: number;
  cls: string;
  faction: Faction;
  at: number;
}

export function rememberDeparture(p: PlayerState, now: number): Departed {
  return { xp: p.xp, level: p.level, cls: p.cls, faction: p.faction, at: now };
}

export function stillRemembered(d: Departed, now: number): boolean {
  return now - d.at <= REJOIN_SECONDS;
}
